import { Flex, Form } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { NOTIFICATION_TYPE_ENUM } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useEffect, useState } from "react";

import { editCompanyFeeForBid } from "services";
import { useStores } from "stores";
import { CustomButton, NumberInput } from "ui";
import { formatWithDecimals, parseErrorMessage, toFraction } from "utils";
import { _greaterOrEqual, _isNumericWithPrecision, _isRequired, _lessThan } from "utils/form/simple-validation";

interface FieldType {
  customerFee: number | string;
  lpFee: number | string;
}

const isInvalidFee = (value: number | string | undefined) => {
  if (value === undefined || value === null || String(value).length === 0) {
    return true;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) || numericValue < 0 || numericValue >= 100;
};

export const EditFeeModal = observer(() => {
  const { actionModals, bidCard } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm<FieldType>();
  const fieldsCustomerFee = Form.useWatch("customerFee", form);
  const fieldsLpFee = Form.useWatch("lpFee", form);

  const disabled = isInvalidFee(fieldsCustomerFee) || isInvalidFee(fieldsLpFee);

  const editFeeHandler = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    const formData = {
      customerFee: toFraction(values.customerFee),
      lpFee: toFraction(values.lpFee),
    };

    await editCompanyFeeForBid({ id: bidCard.bid.id }, formData)
      .then(() => {
        setTimeout(() => bidCard.updateCardById(bidCard.bid.id), 1500);
        handleCancelEditFee();
        openNotification({
          message: "Данные успешно обновлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
      })
      .catch((e) => {
        openNotification({
          type: NOTIFICATION_TYPE_ENUM.ERROR,
          description: parseErrorMessage(e),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancelEditFee = () => {
    actionModals.closeAll();
  };

  useEffect(() => {
    form.setFieldsValue({
      customerFee: formatWithDecimals((bidCard.bid.customerFee ?? 0) * 100, 2),
      lpFee: formatWithDecimals((bidCard.bid.lpFee ?? 0) * 100, 2),
    });
  }, []);
  return (
    <Flex vertical gap="large">
      <Form form={form} name="editFee" onFinish={() => editFeeHandler()}>
        <Flex vertical>
          <Form.Item
            name="customerFee"
            rules={[
              _isRequired("Поле обязательно для ввода"),
              _greaterOrEqual(0),
              _isNumericWithPrecision(),
              _lessThan(100, "Комиссия должна быть меньше 100%"),
            ]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <NumberInput placeholder="Комиссия УВЭД, %" disabled={loading} />
          </Form.Item>
          <Form.Item
            name="lpFee"
            rules={[
              _isRequired("Поле обязательно для ввода"),
              _greaterOrEqual(0),
              _isNumericWithPrecision(),
              _lessThan(100, "Комиссия должна быть меньше 100%"),
            ]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <NumberInput placeholder="Комиссия ПЛ, %" disabled={loading} />
          </Form.Item>
        </Flex>
        <Form.Item>
          <Flex gap="large">
            <CustomButton htmlType="submit" disabled={loading || !!disabled} loading={loading} className="full-width">
              Сохранить изменения
            </CustomButton>
            <CustomButton
              htmlType="button"
              disabled={loading}
              loading={loading}
              type="default"
              onClick={handleCancelEditFee}
              className="full-width"
            >
              Отменить
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  );
});
